import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useSwipeToClose from "../../../hooks/useSwipeToClose";
import { GROCERY_HOST } from "../../../services/apiConfig";
import { getAuthHeaders } from "../utils/productListUtils";

const ReplaceProductDrawer = ({
  isOpen,
  ingredientName,
  currentProductId,
  onProductReplaced,
  closeDrawer,
}) => {
  const {
    containerRef,
    dragOffset,
    isAnimating,
    shouldRender,
    isVisible,
    isDragging,
    DragHandle,
  } = useSwipeToClose(closeDrawer, isOpen);
  const portalContainerRef = useRef(null);
  const [slug, setSlug] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    const container = document.createElement("div");
    container.id = "replace-product-portal";
    container.style.position = "relative";
    container.style.zIndex = "99999";
    document.body.appendChild(container);
    portalContainerRef.current = container;

    return () => {
      if (container && document.body.contains(container)) {
        document.body.removeChild(container);
      }
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSlug("");
      setError("");
      setSuccess("");
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const productSlug = slug.trim();

    if (!productSlug) {
      setError("Please enter a product slug");
      return;
    }

    setError("");
    setSuccess("");
    setIsLoading(true);
    let verificationFailed = false;

    try {
      const response = await fetch(
        `${GROCERY_HOST}/api/products/${encodeURIComponent(productSlug)}`,
        {
          method: "GET",
          headers: {
            Accept: "application/json",
            ...getAuthHeaders(),
          },
        }
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(
          errData.message || `Product not found (HTTP ${response.status})`
        );
      }

      const newProduct = await response.json();
      const normalizedIngredientName = (ingredientName || "").toLowerCase().trim();

      if (normalizedIngredientName) {
        // STEP 1: GET the current (wrong) product first so we can capture its
        // displayName. We add that displayName as an extra keyword on the NEW
        // product as a long-term manual-training signal: future ingredients
        // whose query text resembles the wrong product's name will be more
        // likely to land on the correct product instead.
        let currentProduct = null;
        if (currentProductId && currentProductId !== productSlug) {
          try {
            const currentProductResponse = await fetch(
              `${GROCERY_HOST}/api/products/${encodeURIComponent(currentProductId)}`,
              {
                method: "GET",
                headers: {
                  Accept: "application/json",
                  ...getAuthHeaders(),
                },
              }
            );
            if (currentProductResponse.ok) {
              currentProduct = await currentProductResponse.json();
            }
          } catch (err) {
            console.warn("Error fetching current (wrong) product for training keyword:", err);
          }
        }

        // STEP 2: PATCH new product — add both ingredient name and the wrong
        // product's displayName as keywords (deduped, case-insensitive).
        const existingKeywords = Array.isArray(newProduct.keywords)
          ? newProduct.keywords
          : [];
        const existingLower = new Set(existingKeywords.map((k) => String(k).toLowerCase()));
        const keywordsToAdd = [];
        if (!existingLower.has(normalizedIngredientName)) {
          keywordsToAdd.push(normalizedIngredientName);
          existingLower.add(normalizedIngredientName);
        }
        const wrongProductDisplayName = (currentProduct?.displayName || currentProduct?.name || "")
          .toLowerCase()
          .trim();
        if (wrongProductDisplayName && !existingLower.has(wrongProductDisplayName)) {
          keywordsToAdd.push(wrongProductDisplayName);
          existingLower.add(wrongProductDisplayName);
        }

        if (keywordsToAdd.length > 0) {
          const updateResponse = await fetch(
            `${GROCERY_HOST}/api/products/${encodeURIComponent(newProduct.id || productSlug)}`,
            {
              method: "PATCH",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                ...getAuthHeaders(),
              },
              body: JSON.stringify({
                keywords: [...existingKeywords, ...keywordsToAdd],
              }),
            }
          );

          if (!updateResponse.ok) {
            console.warn(
              "Failed to update replacement product keywords:",
              await updateResponse.text()
            );
          }
        }

        // STEP 3: PATCH current (wrong) product — remove the ingredient keyword
        // so it stops winning Pass 1 for this ingredient.
        if (currentProduct) {
          try {
            const currentKeywords = Array.isArray(currentProduct.keywords)
              ? currentProduct.keywords
              : [];
            const keywordIndex = currentKeywords.findIndex(
              (keyword) => keyword.toLowerCase() === normalizedIngredientName
            );

            if (keywordIndex !== -1) {
              const updatedCurrentKeywords = currentKeywords.filter(
                (_, index) => index !== keywordIndex
              );
              const removeKeywordResponse = await fetch(
                `${GROCERY_HOST}/api/products/${encodeURIComponent(currentProductId)}`,
                {
                  method: "PATCH",
                  headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                    ...getAuthHeaders(),
                  },
                  body: JSON.stringify({ keywords: updatedCurrentKeywords }),
                }
              );

              if (!removeKeywordResponse.ok) {
                console.warn(
                  "Failed to remove keyword from current product:",
                  await removeKeywordResponse.text()
                );
              }

              const removalResponse = await fetch(`${GROCERY_HOST}/api/replacements`, {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  Accept: "application/json",
                  ...getAuthHeaders(),
                },
                body: JSON.stringify({
                  id: `${currentProductId}_${normalizedIngredientName.replace(/\s+/g, "_")}_remove_${Date.now()}`,
                  slug: currentProductId,
                  name: ingredientName,
                  remove: true,
                }),
              });

              if (!removalResponse.ok) {
                console.warn(
                  "Failed to write removal record to replacements collection:",
                  await removalResponse.text()
                );
              }
            }
          } catch (err) {
            console.warn("Error checking/removing keyword from current product:", err);
          }
        }

        const replacementResponse = await fetch(`${GROCERY_HOST}/api/replacements`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            id: `${productSlug}_${normalizedIngredientName.replace(/\s+/g, "_")}_${Date.now()}`,
            slug: productSlug,
            name: ingredientName,
            remove: false,
          }),
        });

        if (!replacementResponse.ok) {
          console.warn(
            "Failed to write replacement record to replacements collection:",
            await replacementResponse.text()
          );
        }

        // STEP 4: Verification — query Typesense as the grocery pipeline would
        // (same query_by + weights as Pass 1 in groceryProcessorMetric.js) and
        // check that the new product is now top-1 for this ingredient. If the
        // wrong product still wins (e.g. very strong displayName/name match),
        // surface a warning so admin knows the replacement may not stick on
        // the next meal-plan regeneration.
        try {
          const verifyResponse = await fetch(
            `${GROCERY_HOST}/api/products/search`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                ...getAuthHeaders(),
              },
              body: JSON.stringify({
                searches: [
                  {
                    collection: "products",
                    q: normalizedIngredientName,
                    query_by: "keywords,displayName,name",
                    query_by_weights: "30,3,3",
                    per_page: 1,
                    prioritize_exact_match: true,
                  },
                ],
              }),
            }
          );
          if (verifyResponse.ok) {
            const verifyData = await verifyResponse.json();
            const topHit = verifyData?.results?.[0]?.hits?.[0]?.document;
            const topHitId = topHit?.id;
            const expectedId = newProduct.id || productSlug;
            if (topHitId && topHitId !== expectedId) {
              verificationFailed = true;
              console.warn(
                `[ReplaceProduct] ⚠️ Verification mismatch: top-1 hit for "${normalizedIngredientName}" is "${topHit.displayName || topHit.name}" (${topHitId}), expected "${newProduct.displayName || newProduct.name}" (${expectedId}). Replacement may not take effect on next regeneration.`
              );
              setError(
                `Warning: Typesense still ranks "${topHit.displayName || topHit.name}" above "${newProduct.displayName || newProduct.name}" for "${ingredientName}". The replacement was saved but may not apply on next regeneration.`
              );
            } else {
              console.log(
                `[ReplaceProduct] ✅ Verified: "${newProduct.displayName || newProduct.name}" (${expectedId}) now wins Pass 1 for "${normalizedIngredientName}".`
              );
            }
          }
        } catch (err) {
          console.warn("Verification query failed:", err);
        }
      }

      setSuccess(`Replaced with "${newProduct.displayName || newProduct.name}"`);
      if (onProductReplaced) {
        onProductReplaced({
          oldProductId: currentProductId,
          newProduct,
          ingredientName,
        });
      }
      // Skip auto-close when verification flagged a mismatch — admin should
      // read the warning and dismiss manually so the issue isn't missed.
      if (!verificationFailed) {
        setTimeout(() => closeDrawer(), 1200);
      }
    } catch (err) {
      setError(err.message || "Failed to replace product");
    } finally {
      setIsLoading(false);
    }
  };

  if (!shouldRender || !portalContainerRef.current) return null;

  return createPortal(
    <div
      className="fixed flex items-end justify-center inset-0 bg-[#0D0C07] drawer-overlay"
      style={{
        backgroundColor: `rgba(13, 12, 7, ${isVisible ? 0.5 : 0})`,
        transition: "background-color 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        zIndex: 99999,
      }}
      onClick={closeDrawer}
    >
      <div
        ref={containerRef}
        className="flex flex-col w-full bg-[#F8F8F7] rounded-[12px_12px_0px_0px] max-w-5xl mx-auto shadow-lg drawer-content max-h-[80dvh]"
        style={{
          transform: isVisible
            ? `translate3d(0, ${Math.max(0, dragOffset)}px, 0)`
            : "translate3d(0, 100%, 0)",
          transition:
            (dragOffset === 0 && isAnimating) ||
            (!isDragging && dragOffset === 0)
              ? "transform 250ms cubic-bezier(0.25, 0.46, 0.45, 0.94)"
              : "none",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <DragHandle />
        <button
          onClick={closeDrawer}
          className="absolute top-1 right-1 flex justify-center items-center md:hover:bg-gray-200 w-[36px] h-[36px] rounded-[12px] transition-colors z-20"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z"
              fill="#56554D"
            />
          </svg>
        </button>

        <div className="flex-1 overflow-y-auto min-h-0 overflow-x-hidden p-6 pt-8">
          <h2 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[18px] font-bold leading-[28px] mb-2">
            Replace Product
          </h2>
          <p className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px] mb-4">
            Enter the product slug to replace the match for "
            <strong>{ingredientName}</strong>"
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="slug-input"
                className="block text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[14px] font-medium mb-2"
              >
                Product Slug *
              </label>
              <input
                id="slug-input"
                type="text"
                value={slug}
                onChange={(e) => setSlug(e.target.value)}
                placeholder="e.g., milk-whole-1l"
                className="w-full px-4 py-3 border border-[var(--Color-Grays-Gray-3,#E8E6E3)] rounded-[8px] text-[14px] focus:outline-none focus:border-[#FFCB00] focus:ring-1 focus:ring-[#FFCB00]"
                disabled={isLoading}
                autoFocus
              />
            </div>

            {error && (
              <div className="bg-[#FEE2E2] text-[#991B1B] px-4 py-3 rounded-[8px] text-[14px]">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-[#DCFCE7] text-[#166534] px-4 py-3 rounded-[8px] text-[14px]">
                {success}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || !slug.trim()}
              className={`w-full py-3 px-4 rounded-[8px] text-[14px] font-semibold transition-colors ${
                isLoading || !slug.trim()
                  ? "bg-[var(--Color-Grays-Gray-3,#E8E6E3)] text-[var(--Color-Grays-Gray-6,#A09F9C)] cursor-not-allowed"
                  : "bg-[#FFCB00] text-[#0D0C07] hover:bg-[#E6B800]"
              }`}
            >
              {isLoading ? "Updating..." : "Replace product"}
            </button>
          </form>
        </div>
      </div>
    </div>,
    portalContainerRef.current
  );
};

export default ReplaceProductDrawer;
